import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { apiFetch } from '../api';
import './Recharge.css';

export default function Recharge() {
  const nav = useNavigate();

  const [fen, setFen] = useState(10000);
  const amount_cent = useMemo(() => {
    const v = parseInt(fen, 10);
    return Number.isFinite(v) && v > 0 ? v : 0;
  }, [fen]);

  const credits = amount_cent;
  const [loading, setLoading] = useState(false);
  const [orderNo, setOrderNo] = useState('');
  const [codeUrl, setCodeUrl] = useState('');
  const [status, setStatus] = useState('idle');
  const [timeLeft, setTimeLeft] = useState(0);
  const [userCredits, setUserCredits] = useState(0);

  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  const fetchCredits = async () => {
    try {
       const r = await apiFetch('/api/auth/me');
       setUserCredits(r?.user?.balance ?? r?.balance);
    } catch (e) {
      console.warn('获取用户积分失败', e.message);
    }
  };

  const startPolling = (ono) => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(async () => {
      try {
        const r = await apiFetch(`/api/pay/order/${ono}`);
        if (r.pay_status === 'paid') {
          setStatus('paid');
          clearInterval(timerRef.current);
          clearInterval(countdownRef.current);
          fetchCredits();
        } else if (['failed', 'closed', 'refunded'].includes(r.pay_status)) {
          setStatus('failed');
          clearInterval(timerRef.current);
          clearInterval(countdownRef.current);
        }
      } catch (e) {
        console.warn('poll error', e.message);
      }
    }, 2000);
  };

  const startCountdown = () => {
    setTimeLeft(300);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setStatus('failed');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    fetchCredits();
    return () => {
      clearInterval(timerRef.current);
      clearInterval(countdownRef.current);
    };
  }, []);

  const createOrder = async () => {
    if (!localStorage.getItem('token')) return nav('/login');
    if (!amount_cent || amount_cent <= 0) return alert('请输入正确的金额（单位：分）');

    setLoading(true);
    setStatus('pending');
    setOrderNo('');
    setCodeUrl('');
    try {
      const resp = await apiFetch('/api/pay/wechat/create', {
        method: 'POST',
        body: {
          type: 'native',
          amount_cent,
          credits,
          description: `积分充值 ${credits} 点`
        }
      });
      setOrderNo(resp.order_no);
      setCodeUrl(resp.code_url);
      startPolling(resp.order_no);
      startCountdown();
    } catch (e) {
      console.error(e);
      alert('创建订单失败：' + e.message);
      setStatus('idle');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(codeUrl);
      alert('支付链接已复制');
    } catch {
      alert('复制失败，请手动复制');
    }
  };

  const yuanDisplay = useMemo(() => (amount_cent / 100).toFixed(2), [amount_cent]);

  const presetOptions = [
    { fen: 1, label: '¥0.01 / 1积分', desc: '长期有效' },
    { fen: 100, label: '¥1 / 100积分', desc: '长期有效' },
    { fen: 500, label: '¥5 / 500积分', desc: '长期有效' },
    { fen: 1000, label: '¥10 / 1000积分', desc: '长期有效' },
    { fen: 5000, label: '¥50 / 5000积分', desc: '长期有效' },
    { fen: 10000, label: '¥100 / 10000积分', desc: '长期有效' },
  ];

  const currentOption = presetOptions.find(opt => opt.fen === fen);

  return (
    <main className="recharge-wrap">
      <header className="recharge-hero">
        <div className="hero-text">
          <span className="hero-pill">会员特权</span>
          <h2 className="title">充值中心</h2>
          <p className="subtitle">选择适合的充值方案，解锁更多 AI 能力。</p>
        </div>
        <div className="credit-card">
          <span className="credit-label">当前积分</span>
          <strong className="credit-value">{userCredits}</strong>
        </div>
      </header>

      <div className="preset-grid">
        {presetOptions.map(opt => (
          <div
            key={opt.fen}
            className={`preset-card ${fen === opt.fen ? 'active' : ''}`}
            onClick={() => setFen(opt.fen)}
            onMouseEnter={() => setFen(opt.fen)}  /* 悬停即选中 */
          >
            {opt.fen === 10000 && <span className="card-badge">推荐</span>}
            <div className="amount">{opt.label}</div>
            <div className="tag">{opt.desc}</div>
            <div className="pay-hover">
              <button className="go-pay" onClick={(e) => { e.stopPropagation(); createOrder(); }}>
                去支付
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 弹框部分 */}
      {status === 'pending' && codeUrl && (
        <div className="modal">
          <div className="modal-content pay-box">
            <button
              className="modal-close"
              onClick={() => {
                setStatus('idle');
                clearInterval(timerRef.current);
                clearInterval(countdownRef.current);
              }}
            >
              ×
            </button>
            <h3 className="modal-title">商品支付</h3>
            <p className="modal-info">需要支付：<span className="highlight">¥{yuanDisplay}</span></p>
            <p>套餐名称：{currentOption?.label}</p>
            <p>套餐描述：{currentOption?.desc}</p>
            <div className="qr-box">
              <QRCode value={codeUrl} size={180} />
            </div>
            <p className="countdown">
              请在 <span>{Math.floor(timeLeft/60)}:{(timeLeft%60).toString().padStart(2,'0')}</span> 内完成支付!
            </p>
            <div className="pay-method">
              <input type="radio" checked readOnly /> 微信支付
            </div>
            <p className="tip">请打开微信扫码支付</p>
            <div className="modal-actions">
              <button onClick={copyLink}>复制支付链接</button>
              <button
                onClick={() => {
                  setStatus('idle');
                  clearInterval(timerRef.current);
                  clearInterval(countdownRef.current);
                }}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {status === 'paid' && (
        <section className="result success">
          <h3>支付成功！</h3>
          <p>订单号：{orderNo}</p>
          <p>最新积分：{userCredits}</p>
          <button onClick={() => nav('/')}>返回首页</button>
        </section>
      )}

      {status === 'failed' && (
        <section className="result failed">
          <h3>订单已关闭/超时</h3>
          <p>请重新发起支付。</p>
          <button
            onClick={() => {
              setStatus('idle');
              setOrderNo('');
              setCodeUrl('');
            }}
          >
            重新发起
          </button>
        </section>
      )}
    </main>
  );
}
